/** Currency-aware KPI thresholds for admin overview insights (not hardcoded INR). */
export type InsightBenchmarks = {
  monthlySalaryPerStaff: number;
  avgBookingValueLow: number;
  avgBookingValueTarget: number;
  upsellLow: number;
  upsellHigh: number;
  adBudgetLow: number;
  adBudgetHigh: number;
  revenuePerCustomerLow: number;
  revenuePerCustomerTarget: number;
  revenuePerCustomerLeader: number;
  revenuePerStaffLow: number;
  revenuePerStaffHigh: number;
  revenuePerStaffThreshold: number;
};

const INR_BENCHMARKS: InsightBenchmarks = {
  monthlySalaryPerStaff: 15000,
  avgBookingValueLow: 3000,
  avgBookingValueTarget: 5000,
  upsellLow: 500,
  upsellHigh: 1000,
  adBudgetLow: 2000,
  adBudgetHigh: 5000,
  revenuePerCustomerLow: 5000,
  revenuePerCustomerTarget: 10000,
  revenuePerCustomerLeader: 15000,
  revenuePerStaffLow: 150000,
  revenuePerStaffHigh: 200000,
  revenuePerStaffThreshold: 80000,
};

const BENCHMARKS_BY_CURRENCY: Record<string, InsightBenchmarks> = {
  INR: INR_BENCHMARKS,
  USD: {
    monthlySalaryPerStaff: 3500,
    avgBookingValueLow: 120,
    avgBookingValueTarget: 200,
    upsellLow: 25,
    upsellHigh: 50,
    adBudgetLow: 150,
    adBudgetHigh: 400,
    revenuePerCustomerLow: 800,
    revenuePerCustomerTarget: 2000,
    revenuePerCustomerLeader: 3000,
    revenuePerStaffLow: 30000,
    revenuePerStaffHigh: 45000,
    revenuePerStaffThreshold: 15000,
  },
  EUR: {
    monthlySalaryPerStaff: 3200,
    avgBookingValueLow: 110,
    avgBookingValueTarget: 180,
    upsellLow: 22,
    upsellHigh: 45,
    adBudgetLow: 140,
    adBudgetHigh: 380,
    revenuePerCustomerLow: 750,
    revenuePerCustomerTarget: 1800,
    revenuePerCustomerLeader: 2800,
    revenuePerStaffLow: 28000,
    revenuePerStaffHigh: 42000,
    revenuePerStaffThreshold: 14000,
  },
  GBP: {
    monthlySalaryPerStaff: 2800,
    avgBookingValueLow: 100,
    avgBookingValueTarget: 170,
    upsellLow: 20,
    upsellHigh: 40,
    adBudgetLow: 120,
    adBudgetHigh: 350,
    revenuePerCustomerLow: 700,
    revenuePerCustomerTarget: 1600,
    revenuePerCustomerLeader: 2500,
    revenuePerStaffLow: 25000,
    revenuePerStaffHigh: 38000,
    revenuePerStaffThreshold: 12000,
  },
  AED: {
    monthlySalaryPerStaff: 12000,
    avgBookingValueLow: 400,
    avgBookingValueTarget: 700,
    upsellLow: 80,
    upsellHigh: 150,
    adBudgetLow: 500,
    adBudgetHigh: 1200,
    revenuePerCustomerLow: 3000,
    revenuePerCustomerTarget: 7000,
    revenuePerCustomerLeader: 11000,
    revenuePerStaffLow: 110000,
    revenuePerStaffHigh: 160000,
    revenuePerStaffThreshold: 55000,
  },
  AUD: {
    monthlySalaryPerStaff: 5200,
    avgBookingValueLow: 180,
    avgBookingValueTarget: 280,
    upsellLow: 35,
    upsellHigh: 70,
    adBudgetLow: 200,
    adBudgetHigh: 500,
    revenuePerCustomerLow: 1200,
    revenuePerCustomerTarget: 2800,
    revenuePerCustomerLeader: 4200,
    revenuePerStaffLow: 45000,
    revenuePerStaffHigh: 65000,
    revenuePerStaffThreshold: 22000,
  },
  SGD: {
    monthlySalaryPerStaff: 4500,
    avgBookingValueLow: 160,
    avgBookingValueTarget: 260,
    upsellLow: 30,
    upsellHigh: 60,
    adBudgetLow: 180,
    adBudgetHigh: 450,
    revenuePerCustomerLow: 1000,
    revenuePerCustomerTarget: 2400,
    revenuePerCustomerLeader: 3600,
    revenuePerStaffLow: 40000,
    revenuePerStaffHigh: 58000,
    revenuePerStaffThreshold: 20000,
  },
};

/** All configurable insight benchmark fields (org + platform admin UIs). */
export const ALL_INSIGHT_BENCHMARK_KEYS: Array<keyof InsightBenchmarks> = [
  'monthlySalaryPerStaff',
  'avgBookingValueLow',
  'avgBookingValueTarget',
  'upsellLow',
  'upsellHigh',
  'adBudgetLow',
  'adBudgetHigh',
  'revenuePerCustomerLow',
  'revenuePerCustomerTarget',
  'revenuePerCustomerLeader',
  'revenuePerStaffLow',
  'revenuePerStaffHigh',
  'revenuePerStaffThreshold',
];

/** Subset highlighted first in compact admin layouts. */
export const PRIMARY_INSIGHT_BENCHMARK_KEYS: Array<keyof InsightBenchmarks> = [
  'monthlySalaryPerStaff',
  'avgBookingValueTarget',
  'revenuePerCustomerLeader',
  'revenuePerStaffThreshold',
  'revenuePerStaffLow',
  'revenuePerStaffHigh',
];

/** Merge platform defaults with per-org overrides (org wins). */
export function mergeInsightBenchmarkOverrides(
  platform?: Partial<InsightBenchmarks>,
  org?: Partial<InsightBenchmarks>,
): Partial<InsightBenchmarks> {
  return { ...(platform ?? {}), ...(org ?? {}) };
}

export function getInsightBenchmarks(
  currencyCode = 'INR',
  overrides?: Partial<InsightBenchmarks>,
): InsightBenchmarks {
  const code = String(currencyCode || 'INR')
    .trim()
    .toUpperCase();
  const base = BENCHMARKS_BY_CURRENCY[code] ?? BENCHMARKS_BY_CURRENCY.USD ?? INR_BENCHMARKS;
  if (!overrides) return base;
  const merged = { ...base };
  for (const [key, value] of Object.entries(overrides) as Array<[keyof InsightBenchmarks, number]>) {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
      merged[key] = value;
    }
  }
  return merged;
}

const BENCHMARK_OVERRIDE_KEYS: Array<keyof InsightBenchmarks> = ALL_INSIGHT_BENCHMARK_KEYS;

/** Parse super-admin platform overrides from app_config JSON. */
export function parseInsightBenchmarkOverrides(raw: unknown): Partial<InsightBenchmarks> {
  if (!raw || typeof raw !== 'object') return {};
  const o = raw as Record<string, unknown>;
  const out: Partial<InsightBenchmarks> = {};
  for (const key of BENCHMARK_OVERRIDE_KEYS) {
    const snake = key.replace(/([A-Z])/g, '_$1').toLowerCase();
    const val = o[key] ?? o[snake];
    if (val == null) continue;
    const n = Number(val);
    if (Number.isFinite(n) && n > 0) out[key] = n;
  }
  return out;
}

export const INSIGHT_BENCHMARK_FIELD_LABELS: Record<keyof InsightBenchmarks, string> = {
  monthlySalaryPerStaff: 'Monthly salary per staff (fallback)',
  avgBookingValueLow: 'Low average booking threshold',
  avgBookingValueTarget: 'Target average booking',
  upsellLow: 'Upsell suggestion (low)',
  upsellHigh: 'Upsell suggestion (high)',
  adBudgetLow: 'Ad campaign budget (low)',
  adBudgetHigh: 'Ad campaign budget (high)',
  revenuePerCustomerLow: 'Revenue per customer (low)',
  revenuePerCustomerTarget: 'Revenue per customer (target)',
  revenuePerCustomerLeader: 'Revenue per customer (leader benchmark)',
  revenuePerStaffLow: 'Revenue per staff year (low)',
  revenuePerStaffHigh: 'Revenue per staff year (high)',
  revenuePerStaffThreshold: 'Revenue per staff alert threshold',
};
